import { TEMPLATE_VARIABLES } from '../../utils/templateFields';

/**
 * Build the "/" menu items for an editor. Items depend on runtime mode (document/template)
 * and on host callbacks stored in `editor.storage.quillRuntime`.
 * @param {import('@tiptap/core').Editor} editor - Editor.
 * @returns {Array<{id: string, title: string, description: string, group: string, keywords: string[], icon: string, run: Function}>} Items.
 */
export function buildSlashItems(editor) {
  const runtime = editor.storage.quillRuntime || {};
  const callbacks = runtime.callbacks || {};
  const isTemplate = runtime.mode === 'template';

  /**
   * Run a chain after deleting the "/query" range.
   * @param {(chain: object) => object} fn - Chain builder.
   * @returns {Function} Item runner.
   */
  const chainRun = (fn) => ({ editor: ed, range }) => fn(ed.chain().focus().deleteRange(range)).run();

  const items = [];

  if (isTemplate) {
    items.push(
      {
        id: 'ai-field-block',
        title: 'AI field (section)',
        description: 'A block the AI fills when generating',
        group: 'Template',
        icon: 'field',
        keywords: ['field', 'prompt', 'ai', 'placeholder', 'section'],
        run: chainRun((c) => c.insertTemplateField({ block: true })),
      },
      {
        id: 'ai-field-inline',
        title: 'AI field (inline)',
        description: 'A short value inside a sentence',
        group: 'Template',
        icon: 'fieldInline',
        keywords: ['field', 'inline', 'prompt', 'ai'],
        run: chainRun((c) => c.insertTemplateField({ block: false })),
      },
      ...TEMPLATE_VARIABLES.map((variable) => ({
        id: `var-${variable.name}`,
        title: `Variable: ${variable.label}`,
        description: `{{${variable.name}}}`,
        group: 'Variables',
        icon: 'variable',
        keywords: ['variable', variable.name, variable.label],
        run: chainRun((c) => c.insertContent(`{{${variable.name}}}`)),
      })),
    );
  }

  items.push(
    {
      id: 'ask-ai',
      title: 'Ask AI',
      description: 'Write or continue with AI',
      group: 'AI',
      icon: 'ai',
      keywords: ['ai', 'ask', 'write', 'continue', 'inky'],
      run: ({ editor: ed, range }) => {
        ed.chain().focus().deleteRange(range).run();
        callbacks.openInlineAi?.({ action: 'continue' });
      },
    },
    { id: 'text', title: 'Text', description: 'Plain paragraph', group: 'Basic', icon: 'text', keywords: ['paragraph', 'p'], run: chainRun((c) => c.setParagraph()) },
    { id: 'h1', title: 'Heading 1', description: 'Large section heading', group: 'Basic', icon: 'h1', keywords: ['title', 'h1'], run: chainRun((c) => c.setHeading({ level: 1 })) },
    { id: 'h2', title: 'Heading 2', description: 'Medium section heading', group: 'Basic', icon: 'h2', keywords: ['subtitle', 'h2'], run: chainRun((c) => c.setHeading({ level: 2 })) },
    { id: 'h3', title: 'Heading 3', description: 'Small section heading', group: 'Basic', icon: 'h3', keywords: ['h3'], run: chainRun((c) => c.setHeading({ level: 3 })) },
    { id: 'bullets', title: 'Bulleted list', description: 'Simple bulleted list', group: 'Lists', icon: 'bullets', keywords: ['ul', 'unordered', 'list'], run: chainRun((c) => c.toggleBulletList()) },
    { id: 'numbers', title: 'Numbered list', description: 'List with numbering', group: 'Lists', icon: 'numbers', keywords: ['ol', 'ordered', 'list'], run: chainRun((c) => c.toggleOrderedList()) },
    { id: 'tasks', title: 'Task list', description: 'Track action items', group: 'Lists', icon: 'tasks', keywords: ['todo', 'checkbox', 'check', 'action'], run: chainRun((c) => c.toggleTaskList()) },
    { id: 'quote', title: 'Quote', description: 'Capture a quotation', group: 'Blocks', icon: 'quote', keywords: ['blockquote', 'citation'], run: chainRun((c) => c.toggleBlockquote()) },
    { id: 'code', title: 'Code block', description: 'Monospaced code', group: 'Blocks', icon: 'code', keywords: ['pre', 'snippet'], run: chainRun((c) => c.toggleCodeBlock()) },
    { id: 'divider', title: 'Divider', description: 'Horizontal rule', group: 'Blocks', icon: 'divider', keywords: ['hr', 'line', 'separator'], run: chainRun((c) => c.setHorizontalRule()) },
    { id: 'table', title: 'Table', description: '3 x 3 table with header', group: 'Insert', icon: 'table', keywords: ['grid', 'rows', 'columns'], run: chainRun((c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true })) },
    { id: 'mermaid', title: 'Diagram', description: 'Mermaid flowchart or sequence', group: 'Insert', icon: 'diagram', keywords: ['mermaid', 'flow', 'chart', 'graph'], run: chainRun((c) => c.insertMermaidBlock()) },
  );

  if (runtime.enableImages !== false && callbacks.openImagePicker) {
    items.push({
      id: 'image',
      title: 'Image',
      description: 'Upload an image',
      group: 'Insert',
      icon: 'image',
      keywords: ['picture', 'photo', 'upload'],
      run: ({ editor: ed, range }) => {
        ed.chain().focus().deleteRange(range).run();
        callbacks.openImagePicker();
      },
    });
  }

  return items;
}
